import { Card, CardBody, Icon } from '@festina-life/ui/structure'
import cx from 'classnames'

import { useLayoutContext } from '../../context/layoutContext'
import { useComparableField } from '../../context/useComparableField'
import {
  isNotRelevantField,
  type TemplateField,
  ValueSetupRole,
} from '../../types/templateModel'
import { ValueSetupDiffIndicators } from '../diffIndicators/ValueSetupDiffIndicators'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateDocumentationTooltip } from '../templateDocumentationTooltip/TemplateDocumentationTooltip'
import { TemplateFieldDocumentation } from '../templateFieldDocumentation/TemplateFieldDocumentation'
import { useFieldDocumentation } from '../templateFieldDocumentation/useFieldDocumentation'
import { TemplateFieldAnchor } from './TemplateFieldAnchor'
import { useSerialisedFieldValue } from './useSerialisedFieldValue'

type Props = {
  field: TemplateField
  isHeaderRow: boolean
}

export const TemplateFieldNameCell = ({ field, isHeaderRow }: Props) => {
  const cellContents = field.name
  const {
    preferences: {
      templates: { isShowingSimplifiedView },
    },
  } = useLayoutContext()
  const comparableField = useComparableField<typeof field>(field.path)
  const { modelFieldType } = useFieldDocumentation(field)
  const valueFromComparable = useSerialisedFieldValue({ field })

  return (
    <div className="relative flex size-full flex-row">
      <TemplateFieldAnchor path={field.path} />
      <div className="grid size-full grid-cols-[minmax(0,1fr)_40px] items-center gap-x-2">
        <TemplateDocumentationTooltip
          tip={
            <Card>
              <CardBody
                innerClassName="grid grid-cols-[auto_1fr] items-start gap-x-10 gap-y-6"
                outerClassName="min-w-250 max-w-(--breakpoint-2xl)"
              >
                <TemplateFieldDocumentation
                  field={field}
                  isExtendedView
                  areFieldsShownAsEditable={false}
                />
              </CardBody>
            </Card>
          }
          className="h-full min-w-0"
        >
          <div className="relative flex size-full min-w-0 items-center justify-between">
            <div className={cx('min-w-0', isHeaderRow ? 'pl-2' : 'pl-4')}>
              {isHeaderRow ? (
                <div
                  className={cx('flex flex-row gap-x-1 items-center min-w-0', {
                    italic:
                      field.templatePropertyDescriptor?.stereoTypes.includes(
                        'OnlyTemplatedTemplateProp',
                      ),
                  })}
                >
                  {field.role === ValueSetupRole.IDENTIFIABLE_SUB_TEMPLATE && (
                    <Icon
                      name="CheckBadge"
                      size="md"
                      color="unset"
                      className="shrink-0"
                    />
                  )}
                  <h4
                    className={cx(
                      'm-0 font-medium truncate',
                      isNotRelevantField(field) ? 'line-through' : undefined,
                    )}
                  >
                    {cellContents}
                  </h4>
                </div>
              ) : (
                <div
                  className={cx(
                    'truncate',
                    isNotRelevantField(field) ? 'line-through' : undefined,
                  )}
                >
                  {cellContents}
                </div>
              )}
              {isShowingSimplifiedView && (
                <div className="truncate text-sm opacity-60">
                  {modelFieldType}
                </div>
              )}
            </div>

            <InheritanceIndicator value={field} className="shrink-0" />
          </div>
        </TemplateDocumentationTooltip>
        <div className="w-10">
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={valueFromComparable}
          />
        </div>
      </div>
    </div>
  )
}
